import { useRef, useState } from "react";
import { Trash2, ArrowUp, ArrowDown, Image as ImageIcon, Plus } from "lucide-react";
import { toast } from "sonner";

const MAX_IMAGES = 5;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function ImageGalleryEditor({ images, onChange }) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const safe = Array.isArray(images) ? images : [];
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const remaining = MAX_IMAGES - safe.length;
      if (remaining <= 0) {
        toast.error(`Maximal ${MAX_IMAGES} Bilder pro Kleid.`);
        return;
      }
      const list = Array.from(files).slice(0, remaining);
      const next = [...safe];
      for (const file of list) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`"${file.name}" ist zu groß (max 5 MB).`);
          continue;
        }
        const b64 = await fileToBase64(file);
        next.push(b64);
      }
      onChange(next.slice(0, MAX_IMAGES));
      if (files.length > remaining) {
        toast.warning(`Nur ${remaining} Bilder hinzugefügt (Max ${MAX_IMAGES} erreicht).`);
      }
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAt = (idx) => {
    const next = safe.filter((_, i) => i !== idx);
    onChange(next);
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= safe.length) return;
    const next = [...safe];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  const addByUrl = () => {
    if (safe.length >= MAX_IMAGES) {
      toast.error(`Maximal ${MAX_IMAGES} Bilder pro Kleid.`);
      return;
    }
    const url = window.prompt("Bild-URL eingeben (https://...)");
    if (!url) return;
    onChange([...safe, url.trim()]);
  };

  return (
    <div data-testid="image-gallery-editor" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Bilder ({safe.length}/{MAX_IMAGES})</div>
          <div className="text-xs text-ink/60 mt-1">
            Erstes Bild = Hauptbild im Katalog. Pfeile zum Sortieren.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="add-image-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={safe.length >= MAX_IMAGES || busy}
            className="inline-flex items-center gap-2 bg-ink text-cream px-4 py-2 text-xs uppercase tracking-widest hover:bg-gold transition-colors disabled:opacity-40"
          >
            <Plus size={12} /> Bild
          </button>
          <button
            type="button"
            data-testid="add-image-url-btn"
            onClick={addByUrl}
            disabled={safe.length >= MAX_IMAGES}
            className="inline-flex items-center gap-2 border border-ink text-ink px-4 py-2 text-xs uppercase tracking-widest hover:bg-ink hover:text-cream transition-colors disabled:opacity-40"
          >
            URL
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        data-testid="image-file-input"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      <div className="grid grid-cols-5 gap-3">
        {Array.from({ length: MAX_IMAGES }).map((_, idx) => {
          const src = safe[idx];
          const slotLabel = idx === 0 ? "Bild 1 · Hauptbild" : `Bild ${idx + 1}`;
          return (
            <div
              key={idx}
              data-testid={`gallery-slot-${idx}`}
              className={`relative aspect-[3/4] border ${src ? "border-[#E5E0D8]" : "border-dashed border-[#E5E0D8] bg-stone-50"} overflow-hidden`}
            >
              {src ? (
                <>
                  <img src={fullUrl(src)} alt={`Slot ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  <span className={`absolute top-1 left-1 ${idx === 0 ? "bg-gold" : "bg-ink/85"} text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5`}>
                    {idx === 0 ? "Haupt" : `Nr.${idx + 1}`}
                  </span>
                  <button
                    type="button"
                    data-testid={`gallery-delete-${idx}`}
                    onClick={() => removeAt(idx)}
                    className="absolute top-1 right-1 w-6 h-6 bg-white/95 border border-[#E5E0D8] hover:bg-red-500 hover:text-white hover:border-red-500 flex items-center justify-center transition-colors"
                    aria-label="Löschen"
                  >
                    <Trash2 size={11} />
                  </button>
                  <div className="absolute bottom-1 left-1 right-1 flex justify-between">
                    <button
                      type="button"
                      data-testid={`gallery-up-${idx}`}
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      className="w-6 h-6 bg-white/95 border border-[#E5E0D8] hover:bg-ink hover:text-white disabled:opacity-30 flex items-center justify-center"
                      aria-label="Nach links"
                    >
                      <ArrowUp size={11} className="-rotate-90" />
                    </button>
                    <button
                      type="button"
                      data-testid={`gallery-down-${idx}`}
                      onClick={() => move(idx, 1)}
                      disabled={idx === safe.length - 1 || idx >= safe.length}
                      className="w-6 h-6 bg-white/95 border border-[#E5E0D8] hover:bg-ink hover:text-white disabled:opacity-30 flex items-center justify-center"
                      aria-label="Nach rechts"
                    >
                      <ArrowDown size={11} className="-rotate-90" />
                    </button>
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => idx === safe.length && fileInputRef.current?.click()}
                  disabled={idx !== safe.length || busy}
                  className="w-full h-full flex flex-col items-center justify-center gap-1 text-ink/40 hover:text-ink hover:bg-white transition-colors disabled:cursor-not-allowed p-2"
                  aria-label={slotLabel}
                >
                  <ImageIcon size={20} />
                  <span className="text-[10px] uppercase tracking-widest text-center leading-tight">
                    {slotLabel}
                  </span>
                  <span className="text-[9px] text-ink/40 mt-1">
                    {idx === safe.length ? "Klick zum Hochladen" : "leer"}
                  </span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {busy && <div className="text-xs text-ink/60">Bilder werden geladen…</div>}
    </div>
  );
}
